/**
 * Recordings page
 */
import Dummy from '@shared/utils/dummy';
import Rail from '@shared/classes/rail';
import Recording from '@shared/types/enums/recording';
import MainTemplate from '@components/templates/main/ui/main';
import keepWatchingRequest from '@shared/api/initial/keepWatchingRequest';
import recommendationsRequest from '@shared/api/recommendations/request';
import recordingsObservable from '@shared/api/recording/recordingsObservable';
import updateRecordings from '@shared/actions/recordings/updateRecordings';
import useComplete from '@shared/components/utils/hooks/useComplete';
import useRailDeletion from '@shared/components/utils/hooks/useRailDeletion';
import { IRecordIdentifier } from '@shared/types/interfaces/record';
import { Observer, Subscription } from 'rxjs';
import { selectCurrentParams } from '@shared/store/slices/router.slice';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from '@shared/store/hooks';
import { userSelector } from '@shared/store/slices/user.slice';

/**
 * Displays the user's recordings, keep watching, recommendations and addons
 */
export default function Recordings() {
  // Selectors
  const { isAnonymous } = useAppSelector(userSelector);
  const currentParams = useAppSelector(selectCurrentParams);

  // State
  const [complete, setComplete] = useComplete();
  const [rails, setRails] = useState<Rail[]>([]);

  // Memoized values
  const dummy = useMemo(() => Dummy.getRecordingRails().data, []);

  // Refs
  const paramsRef = useRef(currentParams);
  const railsRef = useRef(rails);
  paramsRef.current = currentParams;
  railsRef.current = rails;

  // Removes deleted KeepWatching/Recording items from the rendered rails
  useRailDeletion({ rails, setRails });

  // Effect hooks

  useEffect(() => {
    let subscription: Subscription | null;
    // Fetch keep watching data
    if (isAnonymous) {
      setComplete(true);
      return;
    }
    if (!complete) {
      let rails: Rail[] = [];
      Promise.all([keepWatchingRequest(), recommendationsRequest()]).then(
        (data) => {
          // Destructure rails data
          const keepWatching = data[0];
          const recommendations = data[1];

          // Initial observer
          const initialObserver: Observer<{
            data: IRecordIdentifier[] | Rail[];
            type: Recording;
          }> = {
            // Next value
            next: (val) => {
              // Finished recordings rails
              if (val.type === Recording.Finished) {
                if (rails.length) {
                  rails = (val.data as Rail[]).concat(rails);
                } else {
                  rails = rails.concat(val.data as Rail[]);
                }
              }
              // Pending recordings rails
              if (val.type === Recording.Pending) {
                rails = rails.concat(val.data as Rail[]);
              }

              // Update recordings value from store
              updateRecordings(val.data, val.type);
            },
            // Error handling
            error: () => {
              setTimeout(() => {
                setComplete(true);
              }, 500);
            },
            // Complete handling
            complete: () => {
              // Delay so that dummies do not cause a brusque flickering due to fast requests
              setTimeout(() => {
                // Add recommendations rail on top of the rails (2nd position)
                if (recommendations) {
                  rails.unshift(recommendations);
                }
                // Add keep watching rail on top of the rails (1st position)
                if (keepWatching) {
                  rails.unshift(keepWatching);
                }
                // Update rails
                if (rails) {
                  setRails(rails as Rail[]);
                }
                // Complete request
                setComplete(true);
              }, 500);

              return null;
            },
          };
          subscription = recordingsObservable().subscribe(
            initialObserver as Partial<Observer<unknown>>
          );
        }
      );
    } else if (!railsRef.current.length && paramsRef?.current?.rails) {
      setRails((paramsRef?.current?.rails as Rail[]) || []);
    }

    return () => {
      subscription?.unsubscribe();
    };
  }, [complete, isAnonymous]);

  // Render
  return (
    <MainTemplate flags={{ complete }} rails={rails?.length ? rails : dummy} />
  );
}
